import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  type ReactNode,
  type HTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from '@/components/icons';
import { useLatest } from '@/hooks/useLatest';
import './Modal.css';

/**
 * Modal component props
 */
export interface ModalProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'role'> {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;

  /**
   * Callback when the modal should close (close button, Escape, overlay click)
   */
  onClose: () => void;

  /**
   * Modal title.
   *
   * With a title, Modal renders the header (title + close button), wraps
   * `children` in a scrollable body and renders `footer` below it.
   * Without a title, Modal renders `children` as-is so they can compose
   * `ModalHeader` / `ModalBody` / `ModalFooter` themselves.
   */
  title?: ReactNode;

  /**
   * Modal content
   */
  children: ReactNode;

  /**
   * Modal size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Whether to close on overlay click
   * @default true
   */
  closeOnOverlayClick?: boolean;

  /**
   * Whether to close on Escape key
   * @default true
   */
  closeOnEscape?: boolean;

  /**
   * Footer content (typically action buttons) - only used with `title`
   */
  footer?: ReactNode;

  /**
   * ARIA role of the dialog
   * @default 'dialog'
   */
  role?: 'dialog' | 'alertdialog';

  /**
   * Id of the element describing the dialog (aria-describedby)
   */
  descriptionId?: string;
}

/**
 * Shared with ModalHeader so composed headers get the close handler and the
 * id referenced by aria-labelledby.
 */
interface ModalContextValue {
  titleId: string;
  onClose: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

/**
 * Stack of open modals. Only the topmost modal reacts to Escape, so a
 * confirmation dialog opened over another modal closes on its own.
 */
const openModalStack: string[] = [];

/**
 * Modal component for displaying overlay dialogs
 *
 * @example
 * ```tsx
 * // Title form
 * <Modal isOpen={isOpen} onClose={close} title="Add Node" footer={<Button>Save</Button>}>
 *   <form>...</form>
 * </Modal>
 *
 * // Composition form
 * <Modal isOpen={isOpen} onClose={close}>
 *   <ModalHeader>Add Node</ModalHeader>
 *   <ModalBody>...</ModalBody>
 *   <ModalFooter>...</ModalFooter>
 * </Modal>
 * ```
 */
export function Modal({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  footer,
  role = 'dialog',
  descriptionId,
  className = '',
  ...props
}: ModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const modalId = useId();
  const titleId = `${modalId}-title`;
  const onCloseRef = useLatest(onClose);
  const closeOnEscapeRef = useLatest(closeOnEscape);

  // Register as topmost modal, handle Escape, lock scroll and manage focus
  useEffect(() => {
    if (!isOpen) return;

    openModalStack.push(modalId);
    const previousActiveElement = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modalRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      const isTopmost = openModalStack[openModalStack.length - 1] === modalId;
      if (event.key === 'Escape' && isTopmost && closeOnEscapeRef.current) {
        onCloseRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = openModalStack.lastIndexOf(modalId);
      if (index !== -1) openModalStack.splice(index, 1);
      document.body.style.overflow = previousOverflow;
      if (previousActiveElement instanceof HTMLElement) {
        previousActiveElement.focus();
      }
    };
  }, [isOpen, modalId, onCloseRef, closeOnEscapeRef]);

  // Handle overlay click
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (closeOnOverlayClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  const hasTitle = title !== undefined && title !== null;

  const modalContent = (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div
        ref={modalRef}
        className={`modal modal-${size} ${className}`.trim()}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        tabIndex={-1}
        {...props}
      >
        <ModalContext.Provider value={{ titleId, onClose }}>
          {hasTitle ? (
            <>
              <ModalHeader>{title}</ModalHeader>
              <div className="modal-body">{children}</div>
              {footer && <div className="modal-footer">{footer}</div>}
            </>
          ) : (
            children
          )}
        </ModalContext.Provider>
      </div>
    </div>
  );

  // Render in portal
  return createPortal(modalContent, document.body);
}

/**
 * ModalHeader component props
 */
export interface ModalHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /**
   * Close handler; defaults to the enclosing Modal's onClose
   */
  onClose?: () => void;
  /**
   * Whether to render the close button
   * @default true
   */
  showCloseButton?: boolean;
}

/**
 * Modal header with the dialog title (referenced by aria-labelledby) and a close button
 */
export function ModalHeader({
  children,
  className = '',
  onClose,
  showCloseButton = true,
  ...props
}: ModalHeaderProps) {
  const context = useContext(ModalContext);
  const handleClose = onClose ?? context?.onClose;

  return (
    <div className={`modal-header ${className}`.trim()} {...props}>
      <h2 id={context?.titleId} className="modal-title">
        {children}
      </h2>
      {showCloseButton && handleClose && (
        <button
          type="button"
          className="modal-close"
          onClick={handleClose}
          aria-label="Close modal"
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
}

/**
 * ModalBody component props
 */
export interface ModalBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Scrollable modal body
 */
export function ModalBody({ children, className = '', ...props }: ModalBodyProps) {
  return (
    <div className={`modal-body ${className}`.trim()} {...props}>
      {children}
    </div>
  );
}

/**
 * ModalFooter component props
 */
export interface ModalFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Modal footer for action buttons
 */
export function ModalFooter({ children, className = '', ...props }: ModalFooterProps) {
  return (
    <div className={`modal-footer ${className}`.trim()} {...props}>
      {children}
    </div>
  );
}
